<script setup>
import { computed, ref } from "vue"
import { useRoute } from "vue-router"
import { useMutation } from "@vue/apollo-composable"
import ValidationErrors from "@/components/common/ValidationErrors.vue"
import ValidationErrorsClass from "@/assets/js/ValidationErrors.js"
import UPDATE_FORGOTTEN_PASSWORD_MUTATION from "@/graphql/Auth/UpdateForgottenPasswordMutation.gql"
import Login from "@/pages/auth/Login.vue"

const route = useRoute()
const token = ref(route.params.token)

const email = ref(null)
const password = ref(null)
const passwordConfirmation = ref(null)
const validationErrors = ref([]) // Set as an array initially
const loadingMessage = ref({ type: null, message: null })

const { mutate: forgotPasswordMutation, loading: submitIsLoading } =
  useMutation(UPDATE_FORGOTTEN_PASSWORD_MUTATION)

const submit = () => {
  validationErrors.value = [] // Reset errors on submit

  const isPasswordMatch = confirmPasswordMatch()
  const isPasswordSecure = confirmPasswordSecurity()

  if (isPasswordMatch && isPasswordSecure) {
    submitMutation()
  }
}

const submitMutation = () => {
  forgotPasswordMutation({
    input: {
      email: email.value,
      token: token.value,
      password: password.value,
      password_confirmation: passwordConfirmation.value,
    },
  })
    .then(() => {
      loadingMessage.value = {
        type: "success",
        message: "Password has been reset.",
      }
    })
    .catch(error => {
      console.error("Error during mutation", error)
      let errors = new ValidationErrorsClass()
      errors.extractErrorsFrom(error)
      validationErrors.value = errors.errors
      loadingMessage.value = {
        type: "error",
        message:
          validationErrors.value?.extensions?.errors?.token ||
          "An error occurred.",
      }
    })
}

const successfulMessage = computed(() => {
  return loadingMessage.value.type === "success"
})

const confirmPasswordMatch = () => {
  const isValid =
    password.value &&
    password.value.trim() !== "" &&
    passwordConfirmation.value &&
    passwordConfirmation.value.trim() !== ""

  const isMatch = isValid && password.value === passwordConfirmation.value

  if (!isValid) {
    loadingMessage.value = {
      type: "error",
      message: "Password and confirmation cannot be empty.",
    }
    if (!validationErrors.value.includes("password")) {
      validationErrors.value.push("password")
    }
  } else if (!isMatch) {
    loadingMessage.value = {
      type: "error",
      message: "Passwords do not match.",
    }
    if (!validationErrors.value.includes("password")) {
      validationErrors.value.push("password")
    }
  } else {
    validationErrors.value = validationErrors.value.filter(
      error => error !== "password",
    )
    loadingMessage.value = { type: null, message: null }
  }

  return isMatch
}

const confirmPasswordSecurity = () => {
  const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\W).{8,}$/

  const isSecure = passwordPattern.test(password.value)

  if (!isSecure) {
    loadingMessage.value = {
      type: "error",
      message:
        "Password must be at least 8 characters long, include an uppercase letter, a lowercase letter, and a special character.",
    }
    if (!validationErrors.value.includes("password")) {
      validationErrors.value.push("password")
    }
  } else {
    validationErrors.value = validationErrors.value.filter(
      error => error !== "password",
    )
    loadingMessage.value = { type: null, message: null }
  }

  return isSecure
}
</script>

<template>
  <div
    class="flex h-screen flex-1 flex-col items-center justify-center px-6 py-12 lg:px-8 overflow-hidden relative z-100"
  >
    <div v-show="!successfulMessage" class="sm:mx-auto sm:w-full sm:max-w-sm">
      <img
        class="mx-auto h-14 w-auto rounded-[0.25rem]"
        src="@/assets/images/ytz-logo.svg"
        alt="YTZ company logo"
      />
      <h2 class="mt-10 mb-4 text-center text-2xl font-medium">Reset Your Password</h2>
      <p class="text-center text-sm text-base-content/70">Enter your email and new password below</p>
    </div>

    <div class="mt-10 mx-auto w-full max-w-xs md:max-w-sm">
      <form
        v-show="!successfulMessage"
        class="space-y-6 transition-opacity duration-500 ease-in-out opacity-100"
        v-on:submit.prevent="submit"
      >
        <div>
          <label for="email" class="block text-sm">Email address</label>
          <div class="mt-2">
            <input
              v-model="email"
              id="email"
              name="email"
              type="email"
              autocomplete="email"
              class="!text-base-content dark:bg-base-300"
              required
            />
          </div>
        </div>
        <div>
          <label for="password" class="block text-sm">New Password</label>
          <div class="mt-2">
            <input
              v-model="password"
              id="password"
              name="password"
              type="password"
              class="!text-base-content dark:bg-base-300"
              :class="{ 'ring-2 ring-error': validationErrors?.includes('password') }"
              required
            />
          </div>
        </div>
        <div>
          <label for="password-confirmation" class="block text-sm">Confirm New Password</label>
          <div class="mt-2">
            <input
              v-model="passwordConfirmation"
              id="password-confirmation"
              name="password-confirmation"
              type="password"
              class="!text-base-content dark:bg-base-300"
              :class="{ 'ring-2 ring-error': validationErrors?.includes('password') }"
              required
            />
          </div>
        </div>
        <div>
          <XButton
            color="primary"
            type="submit"
            class="w-full"
            :isLoading="submitIsLoading"
          >
            Reset Password
          </XButton>
        </div>
      </form>

      <div class="mt-4">
        <div v-show="loadingMessage.type">
          <Alert
            :type="loadingMessage.type"
            :isShowing="loadingMessage.type"
            :alertMessage="loadingMessage.message"
          />
        </div>
        <div v-show="successfulMessage" class="mt-6">
          <Login height="h-full" />
        </div>
      </div>

      <p class="mt-10 text-center text-sm">
        Remember your password?

        <router-link
          :to="{ name: 'Login' }"
          class="font-semibold leading-6 text-accent hover:text-accent/80 outline-accent"
        >
          Back to Login
        </router-link>
      </p>
    </div>
  </div>
</template>

<style scoped>
input[name="email"],
input[name="password"],
input[name="password-confirmation"] {
  @apply block w-full rounded-md border-0 px-2.5 py-1.5 placeholder:text-base-300/40 focus:ring-2 focus:ring-accent outline-none sm:text-sm sm:leading-6 text-base-300;
}
</style>
